import { useState } from 'react'
import type { Shipment } from '../types'

interface PaginationInfo {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

// Extended shipment with client ownership info
export interface ShipmentWithClient extends Shipment {
  clientId?: string | number
  clientName?: string
  isSecondaryClient?: boolean
}

interface ShipmentListProps {
  shipments: Shipment[]
  onShipmentSelect: (shipment: Shipment) => void
  pagination?: PaginationInfo | null
  onPageChange?: (page: number) => void
  loading?: boolean
  onMultipleInvoiceGenerate?: (shipments: Shipment[]) => void
  onMergedEstimateClearance?: (shipments: Shipment[]) => void
  // Multi-client support
  secondaryShipments?: ShipmentWithClient[]
  secondaryClientName?: string
  primaryClientName?: string
  onMultiClientInvoiceGenerate?: (primaryShipments: Shipment[], secondaryShipments: Shipment[]) => void
}

export default function ShipmentList({
  shipments,
  onShipmentSelect,
  pagination,
  onPageChange,
  loading,
  onMultipleInvoiceGenerate,
  onMergedEstimateClearance,
  secondaryShipments = [],
  secondaryClientName,
  primaryClientName,
  onMultiClientInvoiceGenerate
}: ShipmentListProps) {
  const [selectedShipments, setSelectedShipments] = useState<Set<string>>(new Set())
  const [selectedSecondaryShipments, setSelectedSecondaryShipments] = useState<Set<string>>(new Set())
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false)

  // Check if we're in multi-client mode
  const isMultiClientMode = secondaryShipments.length > 0 && secondaryClientName
  const getStatusBadge = (status: string) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 'bold'
    }

    const lowerStatus = status.toLowerCase()
    
    switch (lowerStatus) {
      case 'created':
        return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404' } // Yellow
      case 'open':
        return { ...baseStyle, backgroundColor: '#f8d7da', color: '#721c24' } // Red
      case 'shipped':
        return { ...baseStyle, backgroundColor: '#cce5ff', color: '#004085' } // Blue
      case 'received':
        return { ...baseStyle, backgroundColor: '#d4edda', color: '#155724' } // Green
      case 'cancelled':
      case 'canceled':
        return { ...baseStyle, backgroundColor: '#f8d7da', color: '#721c24' } // Red
      default:
        return { ...baseStyle, backgroundColor: '#f8f9fa', color: '#6c757d' } // Gray
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleShipmentToggle = (shipmentId: string, isSecondary: boolean = false) => {
    if (isSecondary) {
      const newSelected = new Set(selectedSecondaryShipments)
      if (newSelected.has(shipmentId)) {
        newSelected.delete(shipmentId)
      } else {
        newSelected.add(shipmentId)
      }
      setSelectedSecondaryShipments(newSelected)
    } else {
      const newSelected = new Set(selectedShipments)
      if (newSelected.has(shipmentId)) {
        newSelected.delete(shipmentId)
      } else {
        newSelected.add(shipmentId)
      }
      setSelectedShipments(newSelected)
    }
  }

  const handleSelectAll = () => {
    if (selectedShipments.size === shipments.length) {
      setSelectedShipments(new Set())
    } else {
      setSelectedShipments(new Set(shipments.map(s => s.id)))
    }
  }

  const handleSelectAllSecondary = () => {
    if (selectedSecondaryShipments.size === secondaryShipments.length) {
      setSelectedSecondaryShipments(new Set())
    } else {
      setSelectedSecondaryShipments(new Set(secondaryShipments.map(s => s.id)))
    }
  }

  const handleMultipleInvoiceGenerate = () => {
    // In multi-client mode, use the multi-client handler
    if (isMultiClientMode && onMultiClientInvoiceGenerate) {
      const selectedPrimary = shipments.filter(s => selectedShipments.has(s.id))
      const selectedSecondary = secondaryShipments.filter(s => selectedSecondaryShipments.has(s.id))
      if (selectedPrimary.length > 0 || selectedSecondary.length > 0) {
        onMultiClientInvoiceGenerate(selectedPrimary, selectedSecondary)
      }
    } else {
      const selected = shipments.filter(s => selectedShipments.has(s.id))
      if (selected.length > 0 && onMultipleInvoiceGenerate) {
        onMultipleInvoiceGenerate(selected)
      }
    }
  }

  const handleMergedEstimateClearance = () => {
    const selected = shipments.filter(s => selectedShipments.has(s.id))
    if (selected.length > 0 && onMergedEstimateClearance) {
      onMergedEstimateClearance(selected)
    }
  }

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode)
    setSelectedShipments(new Set())
    setSelectedSecondaryShipments(new Set())
  }

  // Total selected count across both primary and secondary
  const totalSelectedCount = selectedShipments.size + selectedSecondaryShipments.size


  if (shipments.length === 0) {
    return null
  }

  const renderPagination = () => {
    if (!pagination || !onPageChange || pagination.lastPage <= 1) return null

    const pages = []
    const currentPage = pagination.currentPage
    const lastPage = pagination.lastPage
    
    // Always show first page
    if (currentPage > 3) {
      pages.push(1)
      if (currentPage > 4) pages.push('...')
    }
    
    // Show pages around current page
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(lastPage, currentPage + 2); i++) {
      pages.push(i)
    }
    
    // Always show last page
    if (currentPage < lastPage - 2) {
      if (currentPage < lastPage - 3) pages.push('...')
      pages.push(lastPage)
    }

    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: '16px',
        padding: '12px 16px',
        borderTop: '1px solid #e0e0e0'
      }}>
        <div style={{ fontSize: '14px', color: '#666' }}>
          Showing {((currentPage - 1) * pagination.perPage) + 1} to{' '}
          {Math.min(currentPage * pagination.perPage, pagination.total)} of{' '}
          {pagination.total} shipments
        </div>
        
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              backgroundColor: currentPage <= 1 ? '#f5f5f5' : '#fff',
              cursor: currentPage <= 1 || loading ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            Previous
          </button>
          
          {pages.map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' ? onPageChange(page) : undefined}
              disabled={loading}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                backgroundColor: page === currentPage ? '#007bff' : '#fff',
                color: page === currentPage ? '#fff' : '#333',
                cursor: typeof page === 'number' && !loading ? 'pointer' : 'default',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '40px'
              }}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= lastPage || loading}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              backgroundColor: currentPage >= lastPage ? '#f5f5f5' : '#fff',
              cursor: currentPage >= lastPage || loading ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            Next
          </button>
        </div>
      </div>
    )
  }

  // Show multi-select toolbar if either single-client or multi-client invoice generation is available
  const showMultiSelectToolbar = onMultipleInvoiceGenerate || onMultiClientInvoiceGenerate

  return (
    <div className="card">
      {showMultiSelectToolbar && (
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={toggleMultiSelectMode}
              style={{
                padding: '8px 16px',
                border: '1px solid #007bff',
                backgroundColor: isMultiSelectMode ? '#007bff' : '#fff',
                color: isMultiSelectMode ? '#fff' : '#007bff',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {isMultiSelectMode ? 'Exit Multi-Select' : 'Multi-Select Mode'}
            </button>
            
            {isMultiSelectMode && (
              <>
                <button
                  onClick={handleSelectAll}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #6c757d',
                    backgroundColor: '#fff',
                    color: '#6c757d',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {selectedShipments.size === shipments.length ? 'Deselect All Primary' : 'Select All Primary'}
                </button>
                {isMultiClientMode && (
                  <button
                    onClick={handleSelectAllSecondary}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #17a2b8',
                      backgroundColor: '#fff',
                      color: '#17a2b8',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {selectedSecondaryShipments.size === secondaryShipments.length ? 'Deselect All Secondary' : 'Select All Secondary'}
                  </button>
                )}
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {totalSelectedCount} shipment{totalSelectedCount !== 1 ? 's' : ''} selected
                  {isMultiClientMode && totalSelectedCount > 0 && (
                    <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                      ({selectedShipments.size} primary, {selectedSecondaryShipments.size} secondary)
                    </span>
                  )}
                </span>
              </>
            )}
          </div>
          
          {isMultiSelectMode && totalSelectedCount > 0 && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {(onMultipleInvoiceGenerate || onMultiClientInvoiceGenerate) && (
                <button
                  onClick={handleMultipleInvoiceGenerate}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Generate Combined Customs Invoice ({totalSelectedCount} shipment{totalSelectedCount !== 1 ? 's' : ''})
                </button>
              )}
              {onMergedEstimateClearance && !isMultiClientMode && (
                <button
                  onClick={handleMergedEstimateClearance}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    backgroundColor: '#007bff',
                    color: '#fff',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Generate Merged Estimate Clearance ({selectedShipments.size} shipment{selectedShipments.size !== 1 ? 's' : ''})
                </button>
              )}
            </div>
          )}
        </div>
      )}
      
      <table className="table">
        <thead>
          <tr>
            {isMultiSelectMode && <th style={{ width: '40px' }}>Select</th>}
            {isMultiClientMode && <th style={{ width: '120px' }}>Client</th>}
            <th>Reference</th>
            <th>Status</th>
            <th>SKUs / Units</th>
            <th>Created</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {/* Primary Client Section Header */}
          {isMultiClientMode && shipments.length > 0 && (
            <tr>
              <td
                colSpan={isMultiSelectMode ? 7 : 6}
                style={{
                  backgroundColor: '#e8f5e9',
                  fontWeight: 'bold',
                  padding: '10px 16px',
                  borderBottom: '2px solid #4caf50'
                }}
              >
                Primary Client: {primaryClientName || 'Current Client'} ({shipments.length} shipments)
              </td>
            </tr>
          )}

          {/* Primary Client Shipments */}
          {shipments.map((shipment) => (
            <tr key={shipment.id} style={{
              backgroundColor: selectedShipments.has(shipment.id) ? '#e3f2fd' : 'inherit'
            }}>
              {isMultiSelectMode && (
                <td>
                  <input
                    type="checkbox"
                    checked={selectedShipments.has(shipment.id)}
                    onChange={() => handleShipmentToggle(shipment.id, false)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                </td>
              )}
              {isMultiClientMode && (
                <td>
                  <span style={{
                    backgroundColor: '#4caf50',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px'
                  }}>
                    Primary
                  </span>
                </td>
              )}
              <td>
                <strong>{shipment.reference}</strong>
                <br />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  ID: {shipment.id}
                </span>
              </td>
              <td>
                <span style={getStatusBadge(shipment.status)}>
                  {shipment.status}
                </span>
              </td>
              <td>
                <div style={{ fontSize: '14px' }}>
                  <strong>{shipment.skuCount || 0}</strong> SKUs
                  <br />
                  <span style={{ color: '#666', fontSize: '12px' }}>
                    {shipment.itemCount || 0} total units
                  </span>
                </div>
              </td>
              <td>{formatDate(shipment.createdAt)}</td>
              <td>
                <button
                  className="button"
                  onClick={() => onShipmentSelect(shipment)}
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Manage
                </button>
              </td>
            </tr>
          ))}

          {/* Secondary Client Section Header */}
          {isMultiClientMode && secondaryShipments.length > 0 && (
            <tr>
              <td
                colSpan={isMultiSelectMode ? 7 : 6}
                style={{
                  backgroundColor: '#e3f2fd',
                  fontWeight: 'bold',
                  padding: '10px 16px',
                  borderBottom: '2px solid #2196f3',
                  borderTop: '3px solid #e0e0e0'
                }}
              >
                Secondary Client: {secondaryClientName} ({secondaryShipments.length} shipments)
              </td>
            </tr>
          )}

          {/* Secondary Client Shipments */}
          {isMultiClientMode && secondaryShipments.map((shipment) => (
            <tr key={`secondary-${shipment.id}`} style={{
              backgroundColor: selectedSecondaryShipments.has(shipment.id) ? '#bbdefb' : '#f5f5f5'
            }}>
              {isMultiSelectMode && (
                <td>
                  <input
                    type="checkbox"
                    checked={selectedSecondaryShipments.has(shipment.id)}
                    onChange={() => handleShipmentToggle(shipment.id, true)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                </td>
              )}
              <td>
                <span style={{
                  backgroundColor: '#2196f3',
                  color: '#fff',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px'
                }}>
                  Secondary
                </span>
              </td>
              <td>
                <strong>{shipment.reference}</strong>
                <br />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  ID: {shipment.id}
                </span>
              </td>
              <td>
                <span style={getStatusBadge(shipment.status)}>
                  {shipment.status}
                </span>
              </td>
              <td>
                <div style={{ fontSize: '14px' }}>
                  <strong>{shipment.skuCount || 0}</strong> SKUs
                  <br />
                  <span style={{ color: '#666', fontSize: '12px' }}>
                    {shipment.itemCount || 0} total units
                  </span>
                </div>
              </td>
              <td>{formatDate(shipment.createdAt)}</td>
              <td>
                <button
                  className="button"
                  onClick={() => onShipmentSelect(shipment)}
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                  disabled
                  title="Secondary client shipments can only be selected, not managed from here"
                >
                  View Only
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {renderPagination()}
    </div>
  )
}